import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Archive, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const useApi = (url, onExpired) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(url).then(({ data: d }) => setData(d)).catch((e) => { if (e.response?.status === 428) onExpired?.(); else toast.error(errMsg(e)); });
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

export const STATUS_FR = { draft: "Brouillon", test: "Mode test", pending: "En attente de validation", rejected: "Refusée", scheduled: "Programmée", active: "Active", paused: "Suspendue",
  ended: "Terminée", cancelled: "Annulée", archived: "Archivée", approved: "Validé", inactive: "Inactif", suspended: "Suspendu" };
export const PIPE_FR = { a_identifier: "À identifier", a_contacter: "À contacter", contacte: "Contacté", rdv: "Rendez-vous prévu", proposition_prep: "Proposition en préparation",
  proposition_envoyee: "Proposition envoyée", negociation: "En négociation", gagne: "Gagné", perdu: "Perdu", a_relancer: "À relancer", actif: "Partenaire actif", a_renouveler: "À renouveler" };
export const CAT_FR = { restauration: "Restauration", bar_cafe: "Bar / café", commerce: "Boutique physique", ecommerce: "Commerce en ligne", sport: "Équipement sportif",
  habillement: "Habillement", services: "Services", numerique: "Application / numérique", loisirs: "Loisirs", evenements: "Événements", b2b: "Prestataire B2B", tourisme: "Tourisme / hébergement", autre: "Autre" };
export const COUNTRY_OPTS = [["", "—"], ["CA", "Canada"], ["FR", "France"], ["US", "États-Unis"], ["OTHER", "Autre"]];

export const Badge = ({ children, tone = "slate", testId }) => {
  const c = { slate: "bg-white/10 text-slate-200", lime: "bg-[#B7F52A]/15 text-[#B7F52A]", amber: "bg-amber-300/15 text-amber-200", red: "bg-red-400/15 text-red-300", sky: "bg-sky-300/15 text-sky-200" }[tone];
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c}`} data-testid={testId}>{children}</span>;
};

export function FieldInput({ f, value, onChange, testId }) {
  const cls = "mpl-input mt-1";
  if (f.type === "textarea") return <textarea className={`${cls} min-h-[70px]`} maxLength={f.max || 3000} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  if (f.type === "select") return <select className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testId}>{f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  if (f.type === "bool") return <input type="checkbox" className="ml-2 h-4 w-4 accent-[#B7F52A]" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />;
  if (f.type === "multi") return (
    <span className="mt-1 flex flex-wrap gap-3" data-testid={testId}>{f.options.map(([v, l]) => (
      <label key={v} className="flex items-center gap-1.5 text-xs"><input type="checkbox" className="h-4 w-4 accent-[#B7F52A]" checked={(value || []).includes(v)}
        onChange={(e) => onChange(e.target.checked ? [...(value || []), v] : (value || []).filter((x) => x !== v))} data-testid={`${testId}-${v}`} /> {l}</label>))}
    </span>
  );
  if (f.type === "color") return (
    <span className="mt-1 flex items-center gap-2">
      {value ? <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-14 cursor-pointer rounded border border-white/15 bg-transparent" data-testid={testId} />
        : <button type="button" onClick={() => onChange("#B7F52A")} className="mpl-btn-ghost px-3 py-1.5 text-xs" data-testid={`${testId}-pick`}>Choisir une couleur</button>}
      {value && <span className="font-mono text-xs text-slate-300">{value.toUpperCase()}</span>}
      {value && <button type="button" onClick={() => onChange(null)} className="text-xs text-slate-400 underline" data-testid={`${testId}-clear`}>Couleur par défaut</button>}
    </span>
  );
  if (f.type === "multi") return (
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testId}>{f.options.map(([v, l]) => {
      const on = (value || []).includes(v);
      return <button type="button" key={v} onClick={() => onChange(on ? value.filter((x) => x !== v) : [...(value || []), v])} className={`rounded-full border px-2.5 py-1 text-[11px] ${on ? "border-[#B7F52A] bg-[#B7F52A]/15 text-[#B7F52A]" : "border-white/10 text-slate-300"}`} data-testid={`${testId}-${v}`}>{l}</button>;
    })}</div>);
  if (f.type === "tags") return <input className={cls} placeholder="Séparer par des virgules" value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1))} data-testid={testId} />;
  return <input className={cls} type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} data-testid={testId} />;
}

export function EntityForm({ fields, initial, onSubmit, onCancel, testId }) {
  const [v, setV] = useState(initial);
  const submit = (e) => {
    e.preventDefault();
    const out = {};
    fields.forEach((f) => { let x = v[f.k]; if (x === "" && !f.keepEmpty) x = null; if (f.type === "tags") x = (x || []).map((s) => s.trim()).filter(Boolean); out[f.k] = x ?? f.default ?? (f.type === "tags" || f.type === "multi" ? [] : null); });
    onSubmit(out);
  };
  return (
    <form onSubmit={submit} className="mpl-card grid gap-3 p-4 text-xs sm:grid-cols-2" data-testid={testId}>
      {fields.map((f) => (
        <label key={f.k} className={`block text-slate-300 ${f.wide || ["textarea", "multi"].includes(f.type) ? "sm:col-span-2" : ""}`}>{f.label}{f.req && " *"}
          <FieldInput f={f} value={v[f.k]} onChange={(x) => setV({ ...v, [f.k]: x })} testId={`${testId}-${f.k}`} />
          {f.hint && <span className="mt-0.5 block text-[10px] text-slate-500">{f.hint}</span>}
        </label>
      ))}
      <div className="flex gap-2 sm:col-span-2">
        <button className="mpl-btn-primary" data-testid={`${testId}-save`}>Enregistrer</button>
        <button type="button" onClick={onCancel} className="mpl-btn-ghost" data-testid={`${testId}-cancel`}><X size={13} /> Annuler</button>
      </div>
    </form>
  );
}

export function EntityManager({ path, fields, title, columns, toBody = (x) => x, fromItem = (x) => x, extra, onExpired, testId, defaults = {} }) {
  const [rows, load] = useApi(`/superadmin/regie/${path}`, onExpired);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const save = async (body) => {
    try {
      if (edit.id) await api.put(`/superadmin/regie/${path}/${edit.id}`, toBody(body)); else await api.post(`/superadmin/regie/${path}`, toBody(body));
      toast.success("Enregistré"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async (id) => { try { await api.delete(`/superadmin/regie/${path}/${id}`); toast.success("Archivé"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const shown = (rows || []).filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  const csv = () => {
    const keys = fields.map((f) => f.k);
    const lines = [keys.join(";"), ...shown.map((r) => keys.map((k) => `"${String(Array.isArray(r[k]) ? r[k].map((x) => (typeof x === "object" ? x.name : x)).join(", ") : r[k] ?? "").replace(/"/g, '""')}"`).join(";"))];
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv" })); a.download = `${path}.csv`; a.click();
  };
  return (
    <section className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-sm font-bold">{title} {rows && <span className="text-slate-500">({rows.length})</span>}</h3>
        <input className="mpl-input w-48 py-1.5 text-xs" placeholder="Rechercher" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />
        <button onClick={csv} className="mpl-btn-ghost text-xs" data-testid={`${testId}-export`}>Exporter</button>
        <button onClick={() => setEdit({ ...defaults })} className="mpl-btn-primary text-xs" data-testid={`${testId}-new`}><Plus size={13} /> Ajouter</button>
      </div>
      {edit && <EntityForm key={edit.id || "new"} fields={fields} initial={edit} onSubmit={save} onCancel={() => setEdit(null)} testId={`${testId}-form`} />}
      <ul className="space-y-2">
        {shown.map((r) => (
          <li key={r.id} className="mpl-card p-3 text-xs" data-testid={`${testId}-row-${r.id}`}>
            <div className="flex flex-wrap items-start gap-2">
              <div className="min-w-0 flex-1">{columns(r)}</div>
              <button onClick={() => setEdit(fromItem(r))} className="mpl-btn-ghost px-2 py-1 text-xs" aria-label="Modifier" data-testid={`${testId}-edit-${r.id}`}><Pencil size={12} /></button>
              <button onClick={() => window.confirm("Archiver cet élément ?") && archive(r.id)} className="mpl-btn-ghost px-2 py-1 text-xs" aria-label="Archiver" data-testid={`${testId}-archive-${r.id}`}><Archive size={12} /></button>
            </div>
            {extra && extra(r, load)}
          </li>
        ))}
        {rows && !shown.length && <li className="text-xs text-slate-500">Aucun élément.</li>}
      </ul>
    </section>
  );
}
